import useUpgradesStore from "@/store/useUpgradesStore";
import {
	TileEntityParam,
	type TileIndex,
	type TileNeighbour,
} from "@/types/tile";
import { shuffle } from "@/utils/array";
import { formatNumber } from "@/utils/format";
import { FrameName, tilerepoGrasslandFrameNames } from "../config";
import { ResourceType } from "../config/resources";
import { TileVisibilityType } from "../config/terrain";
import { UpgradeIds } from "../config/upgrades";
import TileEntity, {
	type TileEntityWithOnBuildMethod,
	type TileEntityWithOnTickMethod,
} from "../TileEntity";

class LargeBreweryBuildingTileEntity
	extends TileEntity
	implements TileEntityWithOnBuildMethod, TileEntityWithOnTickMethod
{
	public static readonly frameName = FrameName.LARGE_BREWERY;

	public get description(): string {
		return super.description.replace("{number}", formatNumber(5));
	}

	public get suppliesTileIndexes(): Set<TileIndex> {
		return new Set<TileIndex>(
			// zasobuje len 1 inn
			Array.from(super.suppliesTileIndexes).slice(0, 1),
		);
	}

	public get requirements(): Map<ResourceType, number> {
		const requirements = new Map<ResourceType, number>(super.requirements);

		// Apply upgrades
		let connectionRequirement =
			(requirements.get(ResourceType.SETTLEMENT_CONNECTION) as number) ||
			0;
		if (
			useUpgradesStore().getCurrentLevelById(UpgradeIds.farm_roads3) > 0
		) {
			connectionRequirement = 0;
		}
		requirements.set(
			ResourceType.SETTLEMENT_CONNECTION,
			connectionRequirement,
		);

		return requirements;
	}

	public onBuild(): void {
		this.onTick();
		this.onTick();
	}

	public onTick(): void {
		if (!this.range) throw new Error("Range is not defined!");

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				this.range,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				if (
					this.tilerepo.getParam(
						tileIndex,
						TileEntityParam.IS_BLOCKED_BY_ENEMY,
					)
				)
					return null;

				if (this.tilerepo.isTileLocked(tileIndex)) return null;

				const [tileId0, , tileId2] =
					this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId0 || tileId2) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId0);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId: tileId0,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					this.tilerepo.getParam(
						neighbour.tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED,
			) as Array<TileNeighbour>;

		if (!neighbours.length) return;

		const targetTile = shuffle(
			neighbours.filter((neighbour: TileNeighbour): boolean =>
				tilerepoGrasslandFrameNames.has(neighbour.frameName),
			),
		).pop();
		if (!targetTile) return;

		this.tilerepo.setTileByTileCoordinates(
			this.tilerepo.getTileCoordinatesByTileIndex(targetTile.tileIndex),
			FrameName.HOPS_FIELD,
		);
	}
}

export default LargeBreweryBuildingTileEntity;
